import React, { useCallback, useMemo } from "react";
import { Flex, ItemType } from "@thenounproject/lingo-core";
import { Inspectable } from "@constants/Inspector";
import { MetaOptions } from "./DisplayPanel/MetaOptions";
import { GuideOptions } from "./DisplayPanel/GuideOptions";
import { DisplaySizeOptions } from "./DisplayPanel/DisplaySizeOptions";
import { LayoutOptions } from "./DisplayPanel/LayoutOptions";
import { ColorOptions } from "./DisplayPanel/ColorOptions";
import useBatchUpdateAssetsMetadata from "@actions/assets/useBatchUpdateAssetsMetadata";
import useBatchSaveItems, { DisplayProperties } from "@actions/items/useBatchSaveItems";
import { ActionContext } from "./registry/types";

type Props = {
  context: ActionContext;
  onClose?: () => void;
};

const DisplayOptionsPanel: React.FC<Props> = ({ context, onClose }) => {
  const {
    selection,
    location: { inLibrary, inAssetDetail, inGallery, space },
  } = context;
  const [batchUpdateAssets] = useBatchUpdateAssetsMetadata();
  const [batchSaveItems] = useBatchSaveItems();

  const selectionContainsGuidesOrGalleries = useMemo(() => {
    return selection.all.some(i =>
      [ItemType.guide, ItemType.gallery].includes(i.item?.type as ItemType)
    );
  }, [selection.all]);

  const assetItems = useMemo(
    () =>
      inLibrary || inAssetDetail || selectionContainsGuidesOrGalleries
        ? selection.assets
        : selection.assetItems,
    [
      inLibrary,
      inAssetDetail,
      selection.assets,
      selection.assetItems,
      selectionContainsGuidesOrGalleries,
    ]
  );

  const cardBackgroundCompatibleItems = selection.cardBackgroundCompatibleItems;

  const onUpdateDisplayProps = useCallback(
    (displayProperties: DisplayProperties, items: Inspectable[] = assetItems) => {
      const itemsToUpdate = items.filter(i => i.item?.type !== ItemType.heading);
      void batchSaveItems({
        updatedItems: itemsToUpdate.map(i => ({
          uuid: i.item.id,
          display_properties: displayProperties,
        })),
      });
    },
    [assetItems, batchSaveItems]
  );

  const editPreviewBackground = useCallback(
    (background: string) => {
      void batchUpdateAssets(
        assetItems.map(i => ({
          uuid: i.asset.id,
          meta: {
            background_color: background,
          },
        }))
      );
    },
    [assetItems, batchUpdateAssets]
  );

  const editCardBackground = useCallback(
    (background: string | null) => {
      onUpdateDisplayProps(
        {
          card_background_color: background ?? "clear",
        },
        cardBackgroundCompatibleItems
      );
    },
    [cardBackgroundCompatibleItems, onUpdateDisplayProps]
  );

  const onUpdateGuideTitle = useCallback(
    async (title: "DO" | "DON'T", color: "green" | "red") => {
      const updatedItems = selection.guideItems.map(i => ({
        uuid: i.item.id,
        data: { title, color },
      }));
      if (!updatedItems.length) return;
      await batchSaveItems({
        updatedItems,
      });
    },
    [batchSaveItems, selection.guideItems]
  );

  const onUpdateGuideStyle = useCallback(
    async (displayStyle: "image" | "text_only") => {
      const updatedItems = selection.guideItems.map(item => {
        const updates = { display_properties: { display_style: displayStyle } };
        return {
          uuid: item.item.id,
          ...updates,
          asset_uuid: null,
        };
      });
      if (!updatedItems.length) return;

      await batchSaveItems({
        updatedItems,
      });
    },
    [batchSaveItems, selection.guideItems]
  );

  if (selection.all.every(i => i.item?.type === ItemType.heading)) return null;

  return (
    <Flex
      gap="8px"
      backgroundColor="grayLightest"
      flexDirection="column"
      p="m"
      borderBottom="default"
      css={`
        &:empty {
          display: none;
        }
      `}>
      {!inGallery && (
        <>
          <MetaOptions
            context={context}
            onUpdateDisplayProps={onUpdateDisplayProps}
            onClose={onClose}
          />
          <GuideOptions
            inspectables={selection.all}
            onUpdateGuideTitle={onUpdateGuideTitle}
            onUpdateGuideStyle={onUpdateGuideStyle}
          />
          {!inAssetDetail && !inLibrary && (
            <>
              <DisplaySizeOptions
                inspectables={selection.all}
                space={space}
                onUpdateDisplayProps={onUpdateDisplayProps}
              />
              <LayoutOptions
                inspectables={selection.all}
                space={space}
                onUpdateDisplayProps={onUpdateDisplayProps}
              />
            </>
          )}
        </>
      )}
      <ColorOptions
        context={context}
        editPreviewBackground={editPreviewBackground}
        editCardBackground={editCardBackground}
      />
    </Flex>
  );
};

export default DisplayOptionsPanel;
